import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { CheckCircle, Close, Dashboard, AutoAwesome } from '@mui/icons-material';
import FlutterServicesAPI from '../../services/flutterServices';

const SuccessPopup = ({
  open,
  onClose,
  onNavigateToDashboard,
  onNavigateToGenerate,
  onNavigateToPlans,
  starterPackage,
  verificationStatus
}) => {
  // Free tokens are granted only after an admin approves the account
  const isPendingVerification = !!verificationStatus && verificationStatus !== 'approved';
  const isFlutterApp = FlutterServicesAPI.isFlutterApp();
  const tokenLabel = FlutterServicesAPI.getCreditLabel().toLowerCase();
  return (
    <Dialog
      open={open}
      onClose={onClose} // This handles outside clicks and ESC key
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: '#fff', // full screen white backdrop
          },
        },
      }}
      disableEscapeKeyDown={false} // Allow ESC key to close
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '17.39px',
          width: { xs: '95%', sm: '550px' },
          height: { xs: 'auto', sm: 'auto' },
          maxWidth: '550px',
          backgroundColor: 'white',
          m: 1
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 1.5,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: '#651BC6',
            color: 'white',
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { bgcolor: '#5115a3' },
          }}
        >
          <img src="/assets/img/icons/close_icon.svg" alt="Close" style={{ width: 15, height: 15 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CheckCircle sx={{
            fontSize: { xs: 68, sm: 57 }, // Larger icon
            color: '#651BC6',
          }} />
        </Box>
        <Typography sx={{
          fontFamily: 'var(--font-montserrat-bold)',
          fontSize: { xs: '20px', sm: '21px' },
          fontWeight: 700,
          mb: 1,
          color: '#651BC6',
        }}>
          {isPendingVerification ? 'Welcome to Glammify!' : 'Verification Successful!'}
        </Typography>
        <Typography sx={{
          fontFamily: 'var(--font-montserrat-medium)',
          fontSize: { xs: '13px', sm: '15px' },
          fontWeight: 500,
          mb: 3,
          color: '#374151',
        }}>
          {isPendingVerification ? 'Your account has been created. 🎉' : 'Welcome to Glammify! 🎉'}
        </Typography>

        <Box sx={{
          background: 'linear-gradient(90deg, rgba(168, 100, 237, 0.1) 0%, rgba(245, 183, 96, 0.1) 100%)',
          p: 3,
          borderRadius: 2,
          border: '1px solid rgba(168, 100, 237, 0.2)',
          mb: 0
        }}>
          {isPendingVerification ? (
            <>
              <Box sx={{
                display: 'inline-block',
                bgcolor: '#FEF3C7',
                color: '#d97706',
                fontFamily: 'var(--font-montserrat-bold)',
                fontWeight: 700,
                fontSize: '14px',
                px: 1.5,
                py: 0.5,
                borderRadius: '999px',
                mb: 1.5
              }}>
                Pending Verification
              </Box>
              <Typography sx={{
                fontFamily: 'var(--font-montserrat-medium)',
                color: '#374151',
                fontSize: '14px',
                lineHeight: 1.6
              }}>
                Thank you for registering. Our team is currently reviewing your account details.
                You will be notified once your account is verified, and your free {tokenLabel} will
                be credited automatically.
              </Typography>
            </>
          ) : (
          <>
          <Typography sx={{
            fontFamily: 'var(--font-montserrat-bold)',
            fontWeight: 700,
            color: '#651BC6',
            mb: 1,
            letterSpacing: '0.05em',
            fontSize: { xs: '22px', sm: '26px' } // Smaller token amount text on mobile
          }}>
            +{starterPackage.credits} {FlutterServicesAPI.getCreditLabel()}
          </Typography>
          <Typography sx={{
            fontFamily: 'var(--font-montserrat-medium)',
            color: '#374151',
            fontSize: '14px',
            lineHeight: 1.6
          }}>
            You've got {starterPackage.credits} {FlutterServicesAPI.getCreditLabel().toLowerCase()} for free to start creating amazing content!
          </Typography>
          {!FlutterServicesAPI.isFlutterApp() && (
            <Typography sx={{
              fontFamily: 'var(--font-montserrat-medium)',
              color: '#6B7280',
              fontSize: '12px',
              mt: 2
            }}>
              {starterPackage.name} Package • Valid for {starterPackage.validity_days} days
            </Typography>
          )}
          </>
          )}
        </Box>

      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center', mx: 'auto' }}>
          <Button
            variant="outlined"
            onClick={onNavigateToDashboard}
            startIcon={<Dashboard style={{ width: 18, height: 18 }} />}
            sx={{
              flex: 1,
              width: '100%',
              height: '48px !important',
              borderRadius: '10px !important',
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              textTransform: 'none',
              fontFamily: 'var(--font-montserrat-medium)',
              fontSize: '14px',
              fontWeight: 500,
              borderColor: '#651BC6',
              color: '#651BC6',
              '&:hover': {
                borderColor: '#5115a3',
                backgroundColor: 'rgba(101, 27, 198, 0.04)',
              }
            }}
          >
            Go to Dashboard
          </Button>

          {!(isPendingVerification && (isFlutterApp || !onNavigateToPlans)) && (
          <Button
            variant="contained"
            onClick={isPendingVerification ? onNavigateToPlans : onNavigateToGenerate}
            startIcon={<AutoAwesome style={{ width: 18, height: 18 }} />}
            sx={{
              flex: 1,
              width: '100%',
              height: '48px !important',
              borderRadius: '10px !important',
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              textTransform: 'none',
              fontFamily: 'var(--font-montserrat-medium)',
              fontSize: '14px',
              fontWeight: 500,
              background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
                boxShadow: 'none',
              }
            }}
          >
            {isPendingVerification ? 'View Plans' : 'Start to Generate'}
          </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessPopup;
